import { CircularProgress } from '@library/loaders/components';
import { useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { PageHeading, PageTemplate } from 'modules/shared/features/content';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDownIcon, InfoCircleIcon } from '@icon/index';
import { IClient, IProject } from '@api/models';
import useLiveCameraListData from 'modules/live-cameras/hooks/useLiveCameraListData';
import LiveCameraCard from '../LiveCameraCard/LiveCameraCard';
import SelectDropdown from '@components/SelectDropDown/SelectDropDown';
import moment from 'moment-timezone';
import { Tooltip } from '@library/tooltip';

const ACTIVE_DAYS_THRESHOLD = 9;

const LiveCameraList = () => {
    const navigate = useNavigate();
    const { ref, inView } = useInView({ threshold: 0.1 });
    const topBarRef = useRef<HTMLDivElement>(null);

    const [searchParams, setSearchParams] = useSearchParams();
    const initialFilter = searchParams.get('filter') || 'Active';
    const [filterValue, setFilterValue] = useState<string>(initialFilter);

    const { allLiveCameras, isLoading, changeFilterValue } = useLiveCameraListData();

    const filteredCameras = allLiveCameras.filter((camera) => {
        const lastUpdatedMoment = camera.lastUpdated
            ? moment.utc(camera.lastUpdated).tz(camera.timezone || 'UTC')
            : null;
        const daysDiff = lastUpdatedMoment ? moment().diff(lastUpdatedMoment, 'days') : null;

        if (filterValue === 'Active') {
            return lastUpdatedMoment && daysDiff !== null && daysDiff <= ACTIVE_DAYS_THRESHOLD;
        }
        if (filterValue === 'Inactive') {
            return !lastUpdatedMoment || daysDiff === null || daysDiff > ACTIVE_DAYS_THRESHOLD;
        }

        return true;
    });

    const allCount = allLiveCameras.length;

    const activeCount = allLiveCameras.filter((camera) => {
        const lastUpdatedMoment = camera.lastUpdated ? moment.tz(camera.lastUpdated, camera.timezone || 'UTC') : null;
        const daysDiff = lastUpdatedMoment ? moment().diff(lastUpdatedMoment, 'days') : null;
        return lastUpdatedMoment && daysDiff !== null && daysDiff <= ACTIVE_DAYS_THRESHOLD;
    }).length;

    const inactiveCount = allCount - activeCount;

    const filterOptions = [
        { label: `All (${allCount})`, value: 'All' },
        // { label: `Online (${onlineCount})`, value: 'online' },
        // { label: `Offline (${offlineCount})`, value: 'offline' },
        { label: `Active (${activeCount})`, value: 'Active' },
        { label: `Inactive (${inactiveCount})`, value: 'Inactive' },
    ];

    return (
        <PageTemplate ref={topBarRef}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <PageHeading>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <span className="truncate max-w-[85vw] sm:max-w-none">
                            {filterValue} Live Cameras - (
                            {filterValue === 'All'
                                ? allCount
                                : filterValue === 'Active'
                                  ? activeCount
                                  : filterValue === 'Inactive'
                                    ? inactiveCount
                                    : 0}
                            )
                        </span>
                        <Tooltip
                            content={
                                'Active cameras are those that connected to ProgressCenter within the last 9 days.'
                            }
                            placement="right"
                        >
                            <span className="text-grayscale-500 inline-flex">
                                <InfoCircleIcon className="h-4 w-4" />
                            </span>
                        </Tooltip>
                    </div>
                </PageHeading>

                {/* Filter dropdown: full-width on mobile, auto on larger screens */}
                <div className="w-full sm:w-auto">
                    <SelectDropdown
                        className="btn-dropdown btn-secondary btn-secondary-hover w-full sm:w-auto justify-between"
                        value={filterValue}
                        onChange={(value) => {
                            const v = String(value);
                            setFilterValue(v);
                            setSearchParams({ filter: v });
                        }}
                        options={filterOptions}
                        render={(label) => (
                            <span className="flex items-center gap-2 text-grayscale-500 font-medium">
                                View:
                                <span className="text-grayscale-900 font-semibold truncate max-w-[60vw] sm:max-w-none">
                                    {label}
                                </span>
                                <ChevronDownIcon className="text-grayscale-400" strokeWidth="2" />
                            </span>
                        )}
                    />
                </div>
            </div>

            <div
                style={{
                    height: `${window.innerHeight - (+topBarRef?.current?.offsetHeight || 0)}px`,
                }}
            >
                <div className="mt-4 ">
                    {isLoading ? (
                        <div className="flex justify-center py-36">
                            <CircularProgress />
                        </div>
                    ) : allLiveCameras.length > 0 ? (
                        // 12-col grid; mobile = 1 per row; sm = 2 per row; lg = 4 per row
                        <div className="grid grid-cols-12 gap-6 mt-6 empty:hidden">
                            {filteredCameras.map((cctvCamera) => (
                                <div className="col-span-12 sm:col-span-6 lg:col-span-3" key={cctvCamera._id}>
                                    <Link
                                        to={`/clients/${(cctvCamera.client as IClient)?._id}/projects/${
                                            (cctvCamera.project as IProject)?._id
                                        }/cctv-cameras/${cctvCamera._id}`}
                                    >
                                        <LiveCameraCard
                                            cctvCamera={cctvCamera}
                                            clientId={(cctvCamera.client as IClient)?._id}
                                            projectId={(cctvCamera.project as IProject)?._id}
                                            ref={ref}
                                        />
                                    </Link>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex justify-center items-center pt-36 text-lg h-full w-full text-grayscale-500">
                            No Camera's Found
                        </div>
                    )}

                    {/* {!allLiveCameras && !isLoading && (
                        <div className="flex justify-center items-center pt-36 text-lg h-full w-full text-grayscale-500">
                            No Camera's Found
                        </div>
                    )} */}
                </div>
            </div>
        </PageTemplate>
    );
};

export default LiveCameraList;
